import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, Mail, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PublicShell } from "../components/PublicShell";
import { Button } from "../components/ui/Button";
import { Field, TextInput } from "../components/ui/Input";

// Contact submissions hit the shared backend endpoint (POST /api/contact),
// which validates + rate-limits and forwards to CONTACT_TO_EMAIL via Resend.
// In dev VITE_API_ORIGIN is empty → relative, proxied to :3000 by Vite.
const API_ORIGIN = import.meta.env.VITE_API_ORIGIN ?? "";

export function Contact() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const submit = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_ORIGIN}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), subject: subject.trim(), message: message.trim() }),
      });
      if (!res.ok) throw new Error("send_failed");
      return (await res.json()) as { ok: boolean };
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit.mutate();
  }

  const valid = name.trim().length > 0 && /.+@.+\..+/.test(email.trim()) && message.trim().length >= 10;

  return (
    <PublicShell>
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6 sm:py-14">
        <div className="flex items-center gap-2 text-brand-soft">
          <Mail size={18} />
          <span className="text-xs font-semibold uppercase tracking-wider">{t("contact.eyebrow")}</span>
        </div>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-ink sm:text-3xl">{t("contact.title")}</h1>
        <p className="mt-2 text-ink-muted">{t("contact.subtitle")}</p>

        <div className="mt-8 card p-6">
          {submit.isSuccess ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-status-done/10 text-status-done">
                <Check size={24} />
              </span>
              <h2 className="text-lg font-semibold text-ink">{t("contact.successTitle")}</h2>
              <p className="max-w-sm text-sm text-ink-muted">{t("contact.successBody")}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={t("contact.nameLabel")}>
                  <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="name" />
                </Field>
                <Field label={t("contact.emailLabel")}>
                  <TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} maxLength={200} autoComplete="email" />
                </Field>
              </div>
              <Field label={t("contact.subjectLabel")}>
                <TextInput value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder={t("contact.subjectPlaceholder")} />
              </Field>
              <Field label={t("contact.messageLabel")}>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  maxLength={5000}
                  className="ring-focus w-full rounded-md border border-line bg-surface-1 px-3 py-2 text-sm text-ink placeholder:text-ink-faint"
                  placeholder={t("contact.messagePlaceholder")}
                />
              </Field>

              {submit.isError && (
                <p className="text-sm text-status-lost">{t("contact.error")}</p>
              )}

              <div className="flex items-center gap-3">
                <Button type="submit" variant="primary" disabled={!valid || submit.isPending}>
                  <MessageSquare size={15} />
                  {submit.isPending ? t("contact.sending") : t("contact.send")}
                </Button>
                <span className="text-xs text-ink-faint">{t("contact.privacyNote")}</span>
              </div>
            </form>
          )}
        </div>
      </div>
    </PublicShell>
  );
}
